import type { BoxProps, ButtonProps, TextProps } from '@chakra-ui/react';
import { FC, ReactElement, ReactNode } from 'react';
import { Link as ReactDomLink } from 'react-router-dom';
import { MBox, MButton, MHStack, MText } from '.';

export type MEmptyPlaceholderProps = {
  renderEmptyPlaceholder?: null | (() => ReactElement);
  mainMessage?: string | null;
  smallMessage?: string | null;
  btnLabel?: string | null;
  alternateBtnLabel?: string | null;
  onClick?: () => void;
  to?: string;
  alternateTo?: string;
  asChild?: boolean;
  boxProps?: BoxProps;
  mainMessageProps?: TextProps;
  secondaryBtnLabel?: string | null;
  onClickSecondaryBtn?: () => void;
  btnProps?: Omit<ButtonProps, 'variant' | 'as' | 'to' | 'onClick'> & {
    'data-testid': string;
  };
  alternateBtnProps?: Omit<ButtonProps, 'variant' | 'as' | 'to' | 'onClick'> & {
    'data-testid': string;
  };
  childBtnContent?: ReactNode;
};

// pass either 'to' or 'onClick' prop for the button
const MEmptyDataPlaceholder: FC<MEmptyPlaceholderProps> = ({
  mainMessage,
  smallMessage,
  btnLabel,
  alternateBtnLabel,
  to,
  alternateTo,
  mainMessageProps,
  boxProps,
  secondaryBtnLabel,
  onClickSecondaryBtn,
  btnProps,
  alternateBtnProps,
  childBtnContent,
  ...rest
}: MEmptyPlaceholderProps) => {
  return (
    <MBox
      height="50vh"
      display="flex"
      flexDir="column"
      margin="0 auto"
      alignItems="center"
      justifyContent="center"
      {...boxProps}
    >
      <MText fontWeight="bold" fontSize="2xl" {...mainMessageProps}>
        {mainMessage}
      </MText>
      <MText color="tGray.darkGrayPurple">{smallMessage}</MText>
      <MHStack mt={5}>
        {!!btnLabel &&
          (to ? (
            <MButton variant="secondary" {...btnProps} {...rest} asChild>
              <ReactDomLink to={to}>{btnLabel}</ReactDomLink>
            </MButton>
          ) : (
            <MButton variant="secondary" {...btnProps} {...rest}>
              {btnLabel}
            </MButton>
          ))}
        {!!alternateBtnLabel &&
          (alternateTo ? (
            <MButton
              variant="secondary"
              {...alternateBtnProps}
              {...rest}
              asChild
            >
              <ReactDomLink to={alternateTo}>{alternateBtnLabel}</ReactDomLink>
            </MButton>
          ) : (
            <MButton variant="secondary" {...alternateBtnProps} {...rest}>
              {alternateBtnLabel}
            </MButton>
          ))}
      </MHStack>
      {!!secondaryBtnLabel && (
        <MButton
          mt={2}
          variant="link"
          {...btnProps}
          {...rest}
          onClick={onClickSecondaryBtn}
        >
          {secondaryBtnLabel}
        </MButton>
      )}
      {!!childBtnContent && childBtnContent}
    </MBox>
  );
};

export default MEmptyDataPlaceholder;
